/* ============================================================================
   JORNADA Y DESCANSO — límites por jurisdicción.

   PROCEDENCIA Y ADVERTENCIA
   -------------------------
   Estos umbrales están transcritos de un CUADRO COMPARATIVO aportado por el
   operador, no del texto oficial de cada reglamento. Sirven para que el
   selector de jurisdicción deje de ser decorativo —hasta ahora los cuatro
   países tenían las mismas reglas y elegir no cambiaba nada—, pero:

     · No reemplazan al reglamento ni al manual de operaciones aprobado.
     · Cada país tiene excepciones, prórrogas y regímenes especiales que un
       cuadro de una página no puede contener.
     · Ninguna cifra de acá está verificada contra la fuente primaria.

   La aplicación lo dice en pantalla junto a cada resultado. Un límite legal
   mostrado sin decir de dónde salió es peor que no mostrarlo.

   Fuentes citadas por el cuadro:
     Perú     RAP 135, sección 135.920 y ss.
     Chile    DAN 135 + Código del Trabajo, art. 152 ter D
     Ecuador  RDAC Parte 135, subparte F, 135.930
     EE.UU.   14 CFR Part 135, §135.265 / §135.267 / §135.271
   ========================================================================== */

export const JURISDICCIONES = ["Perú", "Chile", "Ecuador", "EE.UU."];

/** Tipos de operación que cambian el límite de vuelo diario. */
/**
 * Tipos de trabajo, que es lo que decide el máximo diario.
 *
 * `magnetometria` se agregó porque el operador la vuela y **no es transporte
 * de pasajeros**: son horas largas a baja altura siguiendo líneas, con una
 * carga de trabajo distinta.
 *
 * Su límite NO está declarado: mientras el operador no diga cuál le aplica, se
 * usa el más exigente que tenga la jurisdicción. Suponer el de operación
 * estándar le daría horas que su reglamento puede no darle.
 */
export const TIPOS_OPERACION_JORNADA = [
  { clave: "estandar", etiqueta: "Transporte de pasajeros / estándar" },
  { clave: "combinado", etiqueta: "Combinado transporte / carga" },
  { clave: "cargaExterna", etiqueta: "Carga externa (línea larga)" },
  {
    clave: "magnetometria",
    etiqueta: "Magnetometría / aerorrelevamiento",
    /* Sin límite propio declarado: hereda el más exigente. */
    heredaMasExigente: true,
    nota: "Límite no declarado por el operador: se aplica el más exigente de la "
      + "jurisdicción. Declaralo en el manual de operaciones para usar el que "
      + "corresponda.",
  },
];

export const REGLAS_JORNADA = {
  "Perú": {
    norma: "RAP 135, sección 135.920 y ss.",
    /** Límite de vuelo en 24 h, en horas, por tipo de operación. */
    vueloMaximoH: { estandar: 8, combinado: 6, cargaExterna: 5 },
    /** Perú no diferencia el límite por cantidad de pilotos en este cuadro. */
    vueloMaximoDosPilotosH: null,
    /**
     * Descanso mínimo entre servicios. El cuadro da un rango de 10 a 12 h
     * "ajustable según horas voladas". Se toma el EXTREMO SUPERIOR: un rango
     * cuyo criterio de ajuste no está en la fuente no se puede resolver
     * automáticamente, y elegir el mínimo sería suponer el caso más cómodo.
     */
    descansoMinimoH: 12,
    descansoEsRango: { min: 10, max: 12 },
    descansoPorPsv: null,
    descansoPorVuelo: null,
    periodico: "Régimen remoto: 10 días de descanso por cada 15 días de servicio continuo. Si la permanencia se extiende hasta 2 días por fuerza mayor, se compensa con 1 día adicional por cada día en exceso.",
  },

  "Chile": {
    norma: "DAN 135 + Código del Trabajo, art. 152 ter D",
    vueloMaximoH: { estandar: 8, combinado: 8, cargaExterna: 8 },
    vueloMaximoDosPilotosH: null,
    notaVuelo: "8 h con tripulación mínima; extensible con tripulación reforzada, según el manual de operaciones aprobado.",
    /** Escalonado por Período de Servicio de Vuelo, no por horas voladas. */
    descansoPorPsv: [
      { hastaPsvH: 7, descansoH: 10 },
      { hastaPsvH: 8, descansoH: 12 },
      { hastaPsvH: 12, descansoH: 15 },
    ],
    descansoMinimoH: 10,
    descansoPorVuelo: null,
    periodico: "Mínimo 10 días de descanso al mes. En ciclos continuos, el descanso debe ser al menos el 40 % de los días trabajados. Ningún descanso entre jornadas puede ser inferior a 10 h en tierra.",
  },

  "Ecuador": {
    norma: "RDAC Parte 135, subparte F, 135.930",
    vueloMaximoH: { estandar: 8, combinado: 8, cargaExterna: 8 },
    vueloMaximoDosPilotosH: 10,
    /** Escalonado por tiempo de vuelo programado. */
    descansoPorVuelo: [
      { menorQueH: 8, descansoH: 9 },
      { menorQueH: 9.0001, descansoH: 10 },
      { menorQueH: Infinity, descansoH: 11 },
    ],
    descansoPorPsv: null,
    descansoMinimoH: 9,
    periodico: "Al menos 24 h consecutivas de descanso en cualquier período de 7 días consecutivos. Si por causas imprevistas en ruta se exceden los límites diarios, el descanso posterior sube a 11, 12 o hasta 16 h.",
  },

  "EE.UU.": {
    norma: "14 CFR Part 135, §135.265 / §135.267 / §135.271",
    vueloMaximoH: { estandar: 8, combinado: 8, cargaExterna: 8 },
    vueloMaximoDosPilotosH: 10,
    descansoMinimoH: 10,
    descansoPorPsv: null,
    descansoPorVuelo: null,
    /**
     * La regla de mirada hacia atrás: las 10 h no se cuentan desde que terminó
     * el servicio anterior, sino DENTRO de las 24 h previas a la hora prevista
     * de finalización del vuelo. Es una condición distinta y más exigente de
     * verificar, y la app no puede darla por cumplida sin la bitácora completa.
     */
    lookBack: { ventanaH: 24, descansoContinuoH: 10 },
    periodico: "13 períodos de 24 h consecutivas de descanso por trimestre calendario (§135.267) y 24 h continuas cada 7 días (§135.265). HEMS (§135.271) tiene reglas propias de turno.",
  },
};

/** Límite de vuelo diario aplicable. */
export function limiteVueloDiario(jurisdiccion, tipoOperacion = "estandar", cantidadPilotos = 1) {
  const regla = REGLAS_JORNADA[jurisdiccion];
  if (!regla) return { horas: null, motivo: "Jurisdicción no reconocida." };

  let porTipo = regla.vueloMaximoH?.[tipoOperacion];
  let heredado = null;

  /* Un tipo de trabajo SIN límite propio declarado hereda el MÁS EXIGENTE de
     la jurisdicción.

     Es el caso de la magnetometría: el operador la vuela y su reglamento no le
     asigna un máximo distinto. Suponer el de operación estándar le daría horas
     que puede no tener; tomar el más exigente es el lado seguro, y se dice
     para que el operador declare el que corresponde. */
  const tipo = TIPOS_OPERACION_JORNADA.find((t) => t.clave === tipoOperacion);
  if (!Number.isFinite(porTipo) && tipo?.heredaMasExigente) {
    const valores = Object.values(regla.vueloMaximoH || {}).filter(Number.isFinite);
    if (valores.length > 0) {
      porTipo = Math.min(...valores);
      heredado = tipo.nota;
    }
  }

  if (!Number.isFinite(porTipo)) return { horas: null, motivo: "Tipo de operación no reconocido." };

  // Con dos pilotos el límite sube solo donde la norma lo contempla, y solo en
  // operación estándar: el cuadro no extiende ese alivio a la carga externa.
  const conDos = cantidadPilotos >= 2 && Number.isFinite(regla.vueloMaximoDosPilotosH) && tipoOperacion === "estandar"
    ? regla.vueloMaximoDosPilotosH
    : null;

  return {
    /* Cuando el límite se heredó, se dice: una cifra sin explicación se toma
       por declarada. */
    heredado,
    horas: conDos ?? porTipo,
    norma: regla.norma,
    motivo: conDos !== null
      ? `${conDos} h con dos pilotos según ${regla.norma}.`
      : `${porTipo} h para ${tipoOperacion === "cargaExterna" ? "carga externa" : tipoOperacion === "combinado" ? "operación combinada" : "operación estándar"} según ${regla.norma}.`,
    nota: regla.notaVuelo || null,
  };
}

/**
 * Descanso requerido antes del siguiente servicio.
 *
 * Cada país lo calcula sobre una base distinta y eso importa: Chile lo escala
 * por el PERÍODO DE SERVICIO (que incluye tierra), Ecuador por el TIEMPO DE
 * VUELO programado. Usar una base por la otra da números equivocados en ambas
 * direcciones.
 */
export function descansoRequerido(jurisdiccion, { horasVuelo = null, horasPsv = null } = {}) {
  const regla = REGLAS_JORNADA[jurisdiccion];
  if (!regla) return { horas: null, base: null, motivo: "Jurisdicción no reconocida." };

  if (regla.descansoPorPsv) {
    if (!Number.isFinite(horasPsv)) {
      return {
        horas: regla.descansoMinimoH, base: "PSV", incierto: true,
        motivo: `Sin el período de servicio de vuelo no se puede escalar el descanso. Se muestra el piso de ${regla.descansoMinimoH} h; el real puede ser mayor.`,
      };
    }
    const tramo = regla.descansoPorPsv.find((t) => horasPsv <= t.hastaPsvH);
    const horas = tramo ? tramo.descansoH : regla.descansoPorPsv[regla.descansoPorPsv.length - 1].descansoH;
    return {
      horas, base: "PSV", incierto: !tramo,
      motivo: tramo
        ? `PSV de ${horasPsv} h → ${horas} h de descanso, según ${regla.norma}.`
        : `PSV de ${horasPsv} h supera el último tramo del cuadro (12 h): se aplica el mayor descanso tabulado, ${horas} h. Verificar el texto de la norma.`,
    };
  }

  if (regla.descansoPorVuelo) {
    if (!Number.isFinite(horasVuelo)) {
      return {
        horas: regla.descansoMinimoH, base: "vuelo", incierto: true,
        motivo: `Sin las horas de vuelo programadas no se puede escalar el descanso. Se muestra el piso de ${regla.descansoMinimoH} h.`,
      };
    }
    const tramo = regla.descansoPorVuelo.find((t) => horasVuelo < t.menorQueH);
    return {
      horas: tramo.descansoH, base: "vuelo", incierto: false,
      motivo: `${horasVuelo} h de vuelo → ${tramo.descansoH} h de descanso, según ${regla.norma}.`,
    };
  }

  return {
    horas: regla.descansoMinimoH,
    base: regla.lookBack ? "mirada hacia atrás" : "fijo",
    incierto: Boolean(regla.lookBack),
    motivo: regla.lookBack
      ? `${regla.lookBack.descansoContinuoH} h continuas dentro de las ${regla.lookBack.ventanaH} h previas al fin previsto del vuelo (${regla.norma}). No alcanza con haber descansado ${regla.lookBack.descansoContinuoH} h en algún momento.`
      : `${regla.descansoMinimoH} h según ${regla.norma}.` + (regla.descansoEsRango
        ? ` El cuadro da un rango de ${regla.descansoEsRango.min} a ${regla.descansoEsRango.max} h ajustable según lo volado; se aplica el extremo superior por no tener el criterio de ajuste.`
        : ""),
  };
}

/**
 * Evalúa a un piloto contra la jurisdicción del vuelo.
 *
 * Devuelve hallazgos, no un semáforo binario: "no sé si cumple" y "no cumple"
 * son cosas distintas y tienen que verse distintas.
 */
export function evaluarJornada({
  jurisdiccion,
  tipoOperacion = "estandar",
  cantidadPilotos = 1,
  horasVueloJornada = null,
  horasPsv = null,
  horasDescansoPrevio = null,
} = {}) {
  const regla = REGLAS_JORNADA[jurisdiccion];
  if (!regla) {
    return { valido: false, hallazgos: [{ nivel: "alto", texto: "Jurisdicción no reconocida." }] };
  }

  const limite = limiteVueloDiario(jurisdiccion, tipoOperacion, cantidadPilotos);
  const descanso = descansoRequerido(jurisdiccion, { horasVuelo: horasVueloJornada, horasPsv });
  const hallazgos = [];

  if (!Number.isFinite(horasVueloJornada)) {
    hallazgos.push({ nivel: "precaucion", texto: "Sin horas de vuelo de la jornada cargadas: no se puede comprobar el límite diario." });
  } else if (Number.isFinite(limite.horas) && horasVueloJornada > limite.horas) {
    hallazgos.push({
      nivel: "alto",
      texto: `${horasVueloJornada} h voladas supera el límite de ${limite.horas} h. ${limite.motivo}`,
    });
  }

  if (!Number.isFinite(horasDescansoPrevio)) {
    hallazgos.push({ nivel: "precaucion", texto: "Sin descanso previo registrado: no se puede comprobar el mínimo." });
  } else if (Number.isFinite(descanso.horas) && horasDescansoPrevio < descanso.horas) {
    hallazgos.push({
      nivel: "alto",
      texto: `${horasDescansoPrevio} h de descanso previo, por debajo de las ${descanso.horas} h requeridas. ${descanso.motivo}`,
    });
  }

  if (descanso.incierto) {
    hallazgos.push({ nivel: "precaucion", texto: descanso.motivo });
  }

  return {
    valido: true,
    norma: regla.norma,
    limiteVueloH: limite.horas,
    motivoLimite: limite.motivo,
    notaLimite: limite.nota,
    descansoRequeridoH: descanso.horas,
    baseDescanso: descanso.base,
    motivoDescanso: descanso.motivo,
    periodico: regla.periodico,
    hallazgos,
    // "Apto" solo si no hay ningún hallazgo. Un dato faltante no es aptitud.
    apto: hallazgos.length === 0,
    horasRestantesH: Number.isFinite(limite.horas) && Number.isFinite(horasVueloJornada)
      ? Math.round((limite.horas - horasVueloJornada) * 10) / 10
      : null,
  };
}
